
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { useToast } from './ui/use-toast';
import { supabase } from '../lib/supabase';

const PLEDGE_OPTIONS = [
  {
    id: 'carpool',
    title: 'Carpool Champion',
    description: 'Fill up your car with mountain buddies!',
    icon: '🚗',
    shareMessage: "Just became a Carpool Champion! 🚗❄️ Taking action to protect our winters by sharing rides to the slopes. One full car = less emissions = more snow! Join me at powderproject.org #PowderPromise"
  },
  {
    id: 'equipment',
    title: 'Gear Guardian',
    description: 'Keep your gear in top shape!',
    icon: '🎿',
    shareMessage: "Proud to be a Gear Guardian! 🎿✨ Keeping my equipment in top shape to reduce waste and protect our mountains. Small actions = big impact! Join the movement at powderproject.org #PowderPromise"
  },
  {
    id: 'waste',
    title: 'Zero Waste Warrior',
    description: 'Keep the mountains clean!',
    icon: '♻️',
    shareMessage: "Just leveled up to Zero Waste Warrior! ♻️🏔️ Committed to keeping our mountains pristine by reducing and properly sorting waste. Be part of the solution at powderproject.org #PowderPromise"
  }
];

const CarpoolGame = ({ onComplete }) => {
  const [passengers, setPassengers] = useState(1);
  const maxPassengers = 4;

  const addPassenger = () => {
    if (passengers < maxPassengers) {
      setPassengers(prev => prev + 1);
      if (passengers + 1 === maxPassengers) {
        onComplete(true);
      }
    }
  };

  return (
    <div className="p-6 bg-white/10 rounded-lg text-center">
      <h3 className="text-xl font-bold text-white mb-4">Fill Your Carpool!</h3>
      <div className="bg-blue-900/50 p-4 rounded-lg mb-4">
        <div className="flex justify-center items-center gap-2">
          {[...Array(maxPassengers)].map((_, i) => (
            <motion.div
              key={i}
              className={`text-4xl ${i < passengers ? 'opacity-100' : 'opacity-30'}`}
              initial={{ scale: 0.8 }}
              animate={{ scale: i < passengers ? 1 : 0.8 }}
            >
              {i === 0 ? '🚗' : '👤'}
            </motion.div>
          ))}
        </div>
      </div>
      <Button
        onClick={addPassenger}
        className="bg-blue-600 hover:bg-blue-700 text-white"
        disabled={passengers >= maxPassengers}
      >
        Add Passenger
      </Button>
    </div>
  );
};

const GearGame = ({ onComplete }) => {
  const [fixed, setFixed] = useState(false);

  const fixGear = () => {
    setFixed(true);
    setTimeout(() => onComplete(true), 1000);
  };

  return (
    <div className="p-6 bg-white/10 rounded-lg text-center">
      <h3 className="text-xl font-bold text-white mb-4">Fix Your Gear!</h3>
      <div className="bg-blue-900/50 p-8 rounded-lg mb-4">
        <motion.div
          className="text-8xl"
          animate={{ rotate: fixed ? 360 : 0 }}
          transition={{ duration: 1 }}
        >
          {fixed ? '✨🎿✨' : '🎿'}
        </motion.div>
      </div>
      <Button
        onClick={fixGear}
        className="bg-blue-600 hover:bg-blue-700 text-white"
        disabled={fixed}
      >
        {fixed ? 'All Fixed!' : 'Fix Equipment'}
      </Button>
    </div>
  );
};

const WasteGame = ({ onComplete }) => {
  const [score, setScore] = useState(0);
  const [currentItem, setCurrentItem] = useState({ type: 'recyclable', icon: '📦' });

  const items = [
    { type: 'recyclable', icon: '📦' },
    { type: 'compost', icon: '🍎' },
    { type: 'trash', icon: '🗑️' }
  ];

  const handleSort = (type) => {
    if (type === currentItem.type) {
      setScore(prev => {
        const newScore = prev + 1;
        if (newScore >= 3) {
          onComplete(true);
          return newScore;
        }
        setCurrentItem(items[Math.floor(Math.random() * items.length)]);
        return newScore;
      });
    }
  };

  return (
    <div className="p-6 bg-white/10 rounded-lg text-center">
      <h3 className="text-xl font-bold text-white mb-4">Sort the Waste!</h3>
      <div className="bg-blue-900/50 p-4 rounded-lg mb-4">
        <motion.div
          key={currentItem.icon}
          className="text-6xl mb-4"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          {currentItem.icon}
        </motion.div>
        <div className="grid grid-cols-3 gap-4">
          <Button onClick={() => handleSort('recyclable')}>♻️</Button>
          <Button onClick={() => handleSort('compost')}>🌱</Button>
          <Button onClick={() => handleSort('trash')}>⚫</Button>
        </div>
      </div>
      <p className="text-blue-200">Score: {score}/3</p>
    </div>
  );
};

const SuccessScreen = ({ pledgeType, onClose }) => {
  const pledge = PLEDGE_OPTIONS.find(p => p.id === pledgeType);
  const { toast } = useToast();

  const handleShare = (platform) => {
    const text = pledge?.shareMessage;
    const url = 'https://powderproject.org';
    
    switch(platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(`${text}\n${url}`);
        toast({
          title: "Copied!",
          description: "Share text copied to clipboard",
        });
        break;
    }
  };

  return (
    <div className="text-center space-y-6">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="text-6xl mb-4"
      >
        {pledge?.icon} 🎉
      </motion.div>
      
      <h2 className="text-3xl font-bold text-white">
        Congratulations, {pledge?.title}!
      </h2>
      
      <p className="text-xl text-blue-200">
        You've taken the first step in preserving winter sports!
      </p>
      
      <div className="bg-white/10 rounded-lg p-6">
        <p className="text-lg text-white mb-4">Share your commitment:</p>
        <div className="bg-white/5 rounded p-4 mb-6">
          <p className="text-blue-200">{pledge?.shareMessage}</p>
        </div>
        
        <div className="grid gap-4">
          <Button
            onClick={() => handleShare('twitter')}
            className="w-full bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white"
          >
            Share on Twitter
          </Button>
          <Button
            onClick={() => handleShare('facebook')}
            className="w-full bg-[#4267B2] hover:bg-[#365899] text-white"
          >
            Share on Facebook
          </Button>
          <Button
            onClick={() => handleShare('copy')}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white"
          >
            Copy to Clipboard
          </Button>
        </div>
      </div>
      
      <Button
        onClick={onClose}
        variant="outline"
        className="w-full border-white/20 text-white hover:bg-white/10"
      >
        Close
      </Button>
    </div>
  );
};

const PledgeForm = ({ onClose }) => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [currentGame, setCurrentGame] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const { toast } = useToast();

  const checkExistingPledge = async (email) => {
    try {
      const { data, error } = await supabase
        .from('pledges')
        .select('*')
        .eq('email', email.toLowerCase())
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        toast({
          title: "You've Been Down This Path Before! ❄️",
          description: "Try a different challenge this time or share your previous pledge!",
          duration: 5000,
        });
        onClose();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking pledge:', error);
      return false;
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter your name to continue.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    const isExisting = await checkExistingPledge(formData.email);
    if (!isExisting) {
      setStep(1);
    }
  };

  const handleGameComplete = async () => {
    try {
      const { error } = await supabase
        .from('pledges')
        .insert([{
          name: formData.name,
          email: formData.email.toLowerCase(),
          pledge_type: currentGame,
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;
      
      // Immediately show success screen after game completion
      setShowSuccess(true);
    } catch (error) {
      console.error('Error saving pledge:', error);
      toast({
        title: "Error",
        description: "Failed to save your pledge. Please try again.",
        variant: "destructive",
      });
    }
  };

  const renderGame = () => {
    switch (currentGame) {
      case 'carpool':
        return <CarpoolGame onComplete={() => {
          handleGameComplete();
          setShowSuccess(true);
        }} />;
      case 'equipment':
        return <GearGame onComplete={() => {
          handleGameComplete();
          setShowSuccess(true);
        }} />;
      case 'waste':
        return <WasteGame onComplete={() => {
          handleGameComplete();
          setShowSuccess(true);
        }} />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-blue-900/90 backdrop-blur-md rounded-xl p-8 max-w-lg w-full mx-4 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white"
        >
          ✕
        </button>

        {showSuccess ? (
          <SuccessScreen 
            pledgeType={currentGame}
            onClose={onClose}
          />
        ) : (
          <>
            {step === 0 && (
              <form onSubmit={handleEmailSubmit} className="space-y-6">
                <h2 className="text-3xl font-bold text-white text-center">Take the Powder Promise</h2>
                <p className="text-blue-200 text-center">
                  Join us in protecting winter sports!
                </p>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
                />
                <input
                  type="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
                />
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Start Your Journey
                </Button>
              </form>
            )}

            {step === 1 && !currentGame && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white text-center">Choose Your Challenge</h2>
                <div className="space-y-4">
                  {PLEDGE_OPTIONS.map((option) => (
                    <motion.button
                      key={option.id}
                      className="w-full p-4 rounded-lg text-left bg-white/10 hover:bg-white/20"
                      onClick={() => setCurrentGame(option.id)}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-2xl">{option.icon}</span>
                        <div>
                          <h3 className="font-bold text-white">{option.title}</h3>
                          <p className="text-sm text-blue-200">{option.description}</p>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && currentGame && renderGame()}
          </>
        )}
      </div>
    </div>
  );
};

export default PledgeForm;
